import { logger } from "@repo/logs";

export type CacheTag = 
  | "bot-category" 
  | "bot-subcategory" 
  | "bot-type" 
  | "bot-pattern"
  | "bot-traffic-stats"
  | "patterns"
  | "property-settings";

export interface RevalidateResponse {
  success: boolean;
  revalidated?: string[];
  error?: string;
  timestamp?: string;
}

export async function revalidateBotCache(
  tags: CacheTag[],
  options: { authToken?: string; internal?: boolean } = {}
): Promise<RevalidateResponse> {
  try {
    const { authToken, internal = true } = options;
    
    const response = await fetch("/api/revalidate/bots", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ tags, internal }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to revalidate cache");
    }

    return await response.json();
  } catch (error) {
    logger.error("Cache revalidation failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// API-specific cache invalidation for Vercel Edge Network
export async function revalidateApiCache(
  tags: string[],
  options: { baseUrl?: string } = {}
): Promise<RevalidateResponse> {
  try {
    const { baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "" } = options;
    
    // Purge cache using Vercel's edge network
    const purgePromises = tags.map(async (tag) => {
      const response = await fetch(`${baseUrl}/api/purge-cache`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tags: [tag] }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to purge cache for tag: ${tag}`);
      }
      
      return tag;
    });

    const purgedTags = await Promise.all(purgePromises);
    
    return {
      success: true,
      revalidated: purgedTags,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    logger.error("API cache revalidation failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// Convenience functions for common revalidation scenarios
export const revalidateBotDirectory = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["bot-category", "bot-subcategory", "bot-type"], options);

export const revalidateBotType = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["bot-type", "patterns"], options);

export const revalidateBotCategory = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["bot-category", "bot-subcategory", "bot-type", "patterns"], options);

export const revalidateBotSubcategory = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["bot-subcategory", "bot-type", "patterns"], options);

export const revalidateBotPattern = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["bot-pattern", "bot-type", "patterns"], options);

// New function specifically for property settings changes
export const revalidatePropertySettings = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["property-settings", "patterns"], options);

// New function specifically for bot traffic stats (called after weekly background job)
export const revalidateBotTrafficStats = (options?: { authToken?: string; internal?: boolean }) =>
  revalidateBotCache(["bot-traffic-stats", "bot-type"], options);

// Server-side revalidation for background jobs (requires full URL)
export async function revalidateBotCacheServer(
  tags: CacheTag[],
  options: { baseUrl: string; internal?: boolean } = { baseUrl: "" }
): Promise<RevalidateResponse> {
  try {
    const { baseUrl, internal = true } = options;
    
    if (!baseUrl) {
      throw new Error("baseUrl is required for server-side cache revalidation");
    }
    
    const response = await fetch(`${baseUrl}/api/revalidate/bots`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ tags, internal }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to revalidate cache");
    }

    return await response.json();
  } catch (error) {
    logger.error("Server-side cache revalidation failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// New function specifically for patterns API cache
export const revalidatePatternsApi = (options?: { baseUrl?: string }) =>
  revalidateApiCache(["patterns", "bot-type", "bot-category", "bot-subcategory", "bot-pattern", "property-settings"], options); 